import { useEffect, useState } from "react";
import { PromptProps } from "react-router";

import useOpener, { Opener } from "@hooks/useOpener";

type Props = {
  hasUnsavedChanges: boolean;
};

type ReturnValue = {
  leavingLocation: string | null;
  opener: Opener;
  promptProps: PromptProps;
};

const useUnsavedChanges = ({ hasUnsavedChanges }: Props) => {
  const opener = useOpener();

  const [leavingLocation, setLeavingLocation] = useState<string | null>(null);

  useEffect(() => {
    // the handler for actually showing the prompt
    // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    // if the form is NOT unchanged, then set the onbeforeunload
    if (hasUnsavedChanges) {
      window.addEventListener("beforeunload", handler);
      // clean it up, if the dirty state changes
      return () => {
        window.removeEventListener("beforeunload", handler);
      };
    }
    // since this is not dirty, don't do anything
    return () => {};
  }, [hasUnsavedChanges]);

  const returnValue: ReturnValue = {
    leavingLocation,
    opener,
    promptProps: {
      message: location => {
        opener.open();
        setLeavingLocation(location.pathname);

        return false;
      },
      when: hasUnsavedChanges
    }
  };

  return returnValue;
};

export default useUnsavedChanges;
